import { Component, Injector } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';

import { ConfirmationModalComponent } from '@app/components/modals/confirmation-modal/confirmation-modal.component';
import { ModalManager, NavigationEvent, NavigationEvents, NavigationService } from '@app/services';
import { BasePageDirective } from '@app/shared/bases/base.page';
import { PAGES } from '@app/shared/enums/pages/pages.enum';
import { getUrlFromMap } from '@app/shared/enums/pages/pages.urls';
import { Buttons } from '@app/shared/models/buttons/buttons';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';

import { ManageVacationsService } from '../../../services/manage-vacations/manage-vacations.service';
import { EditVacationsProposalAction } from '../manage-vacations.constants';
import { IProposalsByOrder } from '../manage-vacations.interface';
import { MotSeasonProposal } from '../manage-vacations.model';

@Component({
  selector: 'app-edit-vacations-proposals',
  templateUrl: './edit-vacations-proposals.page.html',
  styleUrls: ['./edit-vacations-proposals.page.scss']
})
export class EditVacationsProposalsPage extends BasePageDirective {
  seasonProposal!: MotSeasonProposal;
  hasDeletedProposal!: boolean;

  constructor(
    private modalManager: ModalManager,
    private navigationService: NavigationService,
    private manageVacationsService: ManageVacationsService,
    injector: Injector
  ) {
    super(injector);
    this.initSeasonProposal();
  }

  trackByItems(item: MSafeAny) {
    return item;
  }

  private initSeasonProposal() {
    this.manageVacationsService.currentEditingProposal.subscribe((proposal) => (this.seasonProposal = proposal));
    this.seasonProposal = this.manageVacationsService.currentEditingProposal.getValue();
  }

  get disableButton() {
    return !this.manageVacationsService.editedProposal && !this.hasDeletedProposal;
  }

  async checkProposalChanges() {
    if (this.manageVacationsService.editedProposal || this.hasDeletedProposal) {
      const exit = await this.showConfirmationModal(false);

      if (!exit) {
        return;
      }
      this.manageVacationsService.editedProposal = false;
      this.navigationService.navigate(new NavigationEvent(NavigationEvents.SetRoot, { path: PAGES.HOME }));
    } else {
      this.navigationService.goBack();
    }
  }

  onActionClicked(proposal: IProposalsByOrder, action: EditVacationsProposalAction) {
    switch (action) {
      case EditVacationsProposalAction.ADD:
        this.addProposal(proposal.order);
        break;
      case EditVacationsProposalAction.EDIT:
        this.editProposal(proposal.order);
        break;
      case EditVacationsProposalAction.DELETE:
        this.removeProposal(proposal);
        break;
    }
  }

  async saveChanges() {
    const save =
      this.seasonProposal.isMomoUser || this.seasonProposal.hasAllProposalsFullfilled()
        ? true
        : await this.showConfirmationModal(true);

    if (save) {
      this.navigationService.goBack(getUrlFromMap(PAGES.VACATIONS_REQUEST), {
        action:
          this.hasDeletedProposal && !this.manageVacationsService.editedProposal
            ? EditVacationsProposalAction.DELETE
            : EditVacationsProposalAction.SAVE_CHANGES,
        seasonProposal: this.seasonProposal
      });

      this.manageVacationsService.editedProposal = false;
    }
  }

  private addProposal(order: number) {
    if (!this.seasonProposal.proposals.length) {
      order = 1;
    }

    this.navigationService.goBack(getUrlFromMap(PAGES.VACATIONS_REQUEST), {
      action: EditVacationsProposalAction.ADD,
      seasonProposal: this.seasonProposal,
      order
    });
  }

  private editProposal(order: number) {
    this.navigationService.goBack(getUrlFromMap(PAGES.VACATIONS_REQUEST), {
      action: EditVacationsProposalAction.EDIT,
      seasonProposal: this.seasonProposal,
      order
    });
  }

  private removeProposal(proposal: IProposalsByOrder) {
    const title = 'MANAGE_VACATIONS.DELETE_CONFIRMATION_TITLE';
    const contents = [];
    const buttons: Buttons[] = [
      {
        text: 'NO',
        type: 'secondary',
        enabled: true,
        onClick: () => {
          this.modalManager.dismissMatModal();
        }
      },
      {
        text: 'YES',
        type: 'primary',
        enabled: true,
        onClick: () => {
          this.seasonProposal.proposals = this.seasonProposal.proposals.filter(
            (savedProposal) => savedProposal.order !== proposal.order
          );
          this.seasonProposal.proposalsByOrder[proposal.order - 1].proposals = [];

          this.navigationService.goBack(getUrlFromMap(PAGES.VACATIONS_REQUEST), {
            seasonProposal: this.seasonProposal,
            action: EditVacationsProposalAction.DELETE
          });
          this.hasDeletedProposal = true;

          this.reorderProposals();
          this.modalManager.dismissMatModal();
        }
      }
    ];
    const modalOpts: MatDialogConfig = {
      data: { title, contents, buttons },
      disableClose: true,
      panelClass: 'media-content-modal'
    };

    this.modalManager.openMatModal(ConfirmationModalComponent, modalOpts);
  }

  private showConfirmationModal(sending: boolean) {
    const title = `MANAGE_VACATIONS.${sending ? 'SEND_PROPOSAL_CONFIRMATION' : 'EXIT_SEND_PROPOSAL_CONFIRMATION'}`;
    const contents = sending
      ? ['MANAGE_VACATIONS.SEND_PROPOSAL_CONFIRMATION_MSG_1', 'MANAGE_VACATIONS.SEND_PROPOSAL_CONFIRMATION_MSG_2']
      : ['MANAGE_VACATIONS.PENDING_CHANGES'];
    const buttons: Buttons[] = [
      {
        text: 'NO',
        type: 'secondary',
        enabled: true,
        onClick: () => {
          this.modalManager.dismissMatModal();
        }
      },
      {
        text: 'YES',
        type: 'primary',
        enabled: true,
        onClick: () => {
          this.modalManager.dismissMatModal(true);
        }
      }
    ];

    const modalOpts: MatDialogConfig = {
      data: { title, contents, buttons },
      disableClose: true,
      panelClass: ['media-content-modal', 'confirmation-two-lines-modal']
    };

    return this.modalManager.openMatModal(ConfirmationModalComponent, modalOpts);
  }

  private reorderProposals() {
    this.seasonProposal.proposalsByOrder.forEach((orderedProposal) => {
      if (orderedProposal.proposals.length) {
        for (let i = orderedProposal.order; i >= 1; i--) {
          if (!this.seasonProposal.proposalsByOrder[i - 1].proposals.length) {
            this.seasonProposal.proposalsByOrder[i - 1].proposals = orderedProposal.proposals.map((proposal) => {
              if (proposal.order) {
                proposal.order--;
              }
              return proposal;
            });

            orderedProposal.proposals = [];
          }
        }
      }
    });
  }
}
